import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { API } from "../../App";

const today = () => new Date().toISOString().slice(0, 10);

export const StInventoryForm = ({ patientId, instrument, spec, onCancel, onSaved }) => {
  const [mode, setMode] = useState("items");
  const [responses, setResponses] = useState({});
  const [scores, setScores] = useState({});
  const [date, setDate] = useState(today());
  const [label, setLabel] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const answered = Object.keys(responses).length;
  const filled = answered > 0 || Object.values(scores).some((v) => v !== "");
  const back = () => { if (!filled || window.confirm("Quitter sans enregistrer ? Les réponses saisies seront perdues.")) onCancel(); };
  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/patients/${patientId}/clinical/st-questionnaires`, { instrument, mode, responses, scores, date, label, note }, { withCredentials: true });
      toast.success("Passation enregistrée"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'enregistrement"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="stq-form">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Button variant="ghost" size="sm" onClick={back} className="gap-1" data-testid="stq-form-back"><ArrowLeft className="w-4 h-4" /> Retour</Button>
        <div className="flex rounded-lg border overflow-hidden text-xs" data-testid="stq-form-mode">
          <button onClick={() => setMode("items")} className={`px-3 py-1.5 ${mode === "items" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="stq-mode-items">Passer les items</button>
          <button onClick={() => setMode("scores")} className={`px-3 py-1.5 ${mode === "scores" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="stq-mode-scores">Saisir des scores</button>
        </div>
      </div>
      <div className="rounded-xl border bg-card p-4 space-y-3">
        <h3 className="font-semibold">{spec.label}</h3>
        <div className="grid sm:grid-cols-3 gap-2">
          <label className="text-xs space-y-1"><span className="text-muted-foreground">Date</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full rounded-md border bg-background px-2 py-1.5 text-sm" data-testid="stq-form-date" /></label>
          <label className="text-xs space-y-1"><span className="text-muted-foreground">Repère (ex. début de thérapie)</span><input value={label} onChange={(e) => setLabel(e.target.value)} className="w-full rounded-md border bg-background px-2 py-1.5 text-sm" data-testid="stq-form-label" /></label>
          <label className="text-xs space-y-1"><span className="text-muted-foreground">Note</span><input value={note} onChange={(e) => setNote(e.target.value)} className="w-full rounded-md border bg-background px-2 py-1.5 text-sm" data-testid="stq-form-note" /></label>
        </div>
      </div>
      {mode === "items" ? (
        <>
          <div className="rounded-xl border bg-muted/40 p-3 text-sm space-y-1">
            <p>{spec.intro}</p>
            <p className="text-xs text-muted-foreground">{spec.scale.map((s) => `${s.value} = ${s.label}`).join(" · ")}</p>
          </div>
          <div className="space-y-2">
            {spec.items.map((it, i) => (
              <div key={it.id} className="rounded-lg border bg-card px-3 py-2 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`stq-item-${it.id}`}>
                <p className="text-sm flex-1"><span className="text-muted-foreground mr-1">{i + 1}.</span>{it.text}</p>
                <div className="flex gap-1 shrink-0">
                  {spec.scale.map((s) => (
                    <button key={s.value} title={s.label} onClick={() => setResponses({ ...responses, [it.id]: s.value })} className={`w-8 h-8 rounded-md text-sm border transition-colors ${responses[it.id] === s.value ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`} data-testid={`stq-item-${it.id}-${s.value}`}>{s.value}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="rounded-xl border bg-card p-4 space-y-2">
          <p className="text-sm text-muted-foreground">Score moyen 1-6 par dimension (instrument officiel passé hors application). Laissez vide les dimensions non cotées.</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {spec.dims.map((d) => (
              <label key={d.key} className="flex items-center justify-between gap-2 rounded-lg border px-3 py-1.5 text-sm" data-testid={`stq-score-${d.key}`}>
                <span>{d.label}</span>
                <input type="number" min="1" max="6" step="0.1" value={scores[d.key] ?? ""} onChange={(e) => setScores({ ...scores, [d.key]: e.target.value })} className="w-20 rounded-md border bg-background px-2 py-1 text-sm text-right" data-testid={`stq-score-input-${d.key}`} />
              </label>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-2 rounded-xl border bg-background/95 backdrop-blur p-3 sticky bottom-2 z-10 shadow-sm" data-testid="stq-form-footer">
        <p className="text-xs text-muted-foreground" data-testid="stq-form-progress">{mode === "items" ? `${answered}/${spec.items.length} items renseignés (minimum 80 %)` : `${Object.values(scores).filter((v) => v !== "").length} score(s) saisi(s)`}</p>
        <Button onClick={submit} disabled={busy || (mode === "items" && answered < spec.items.length * 0.8)} className="gap-1.5" data-testid="stq-form-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer la passation</Button>
      </div>
    </div>
  );
};
